// The card's footer More menu: "Unsubscribe…" and "Report as spam…" for the sender, as the
// alternatives to archiving. Both only OPEN a dialog; the dialogs keep their own arm → confirm
// (unsubscribe's confirm step, spam's reviewed preview and fingerprint), so the menu item decides
// nothing. Rendered as Menu children: they stay mounted while the menu is closed, and the dialogs
// sit in the top layer, so the closed menu's clipped box does not hide them.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { CardView } from "@shared/api";
import { useSender } from "@web/lib/sendersQueries";
import { useCardSender } from "@web/notify/SenderLinks";
import { SenderMailDialog } from "@web/screener/SenderMailDialog";
import { UnsubscribeButton } from "@web/unsubscribe/UnsubscribeButton";
import { MENU_ITEM_CLASS } from "@web/ui/Menu";

/** Mounted only once asked for: loads the sender record, then opens the spam dialog on its arm step. */
function SpamDialog({
  accountId,
  address,
  onClose,
}: {
  accountId: string;
  address: string;
  onClose: () => void;
}) {
  const sender = useSender(accountId, address);
  const unavailable = sender.isError || (sender.isSuccess && sender.data === null);
  useEffect(() => {
    if (!unavailable) return;
    toast("Couldn't open the spam report for this sender. Nothing changed.");
    onClose();
  }, [unavailable, onClose]);
  if (!sender.data) return null;
  return <SenderMailDialog sender={sender.data} initialSpam onClose={onClose} />;
}

function ReportSpamItem({ accountId, address }: { accountId: string; address: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={MENU_ITEM_CLASS}
        aria-label={`Report ${address} as spam`}
        onClick={() => setOpen(true)}
      >
        Report as spam…
      </button>
      {open ? (
        <SpamDialog accountId={accountId} address={address} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/** Menu children for the sender; nothing until the card's thread says who wrote. */
export function SenderTools({ card }: { card: CardView }) {
  const sender = useCardSender(card);
  if (!sender) return null;
  return (
    <>
      <UnsubscribeButton
        variant="menu"
        accountId={sender.accountId}
        accountEmail={sender.accountEmail}
        address={sender.address}
        {...(sender.name ? { name: sender.name } : {})}
        threadKey={sender.threadKey}
      />
      <ReportSpamItem accountId={sender.accountId} address={sender.address} />
    </>
  );
}
